/*
  <litmus-scenes> — sections/litmus-scenes.liquid. Deferred, dependency-free.

  Tiles: the toggle opens and closes a tile's drawer (click, tap, Enter, Space).
  On hover-capable fine pointers, hovering a tile or moving keyboard focus into
  it also opens it. One tile is open at a time. Escape closes. A closed drawer
  is inert, so its link is out of the tab order and the accessibility tree.

  Rail (below 750px): previous/next buttons and a "1 / 4" counter for the
  swipe rail. The buttons are hidden at wider sizes, where the grid is static.

  Without this file the section still works: CSS opens a tile on hover and on
  focus inside it, and nothing is inert.
*/
(() => {
  if (customElements.get('litmus-scenes')) return;

  class LitmusScenes extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const signal = this.controller.signal;

      this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.tiles = Array.from(this.querySelectorAll('[data-scene]'));

      this.tiles.forEach((tile) => this.setupTile(tile, signal));
      this.setupRail(signal);

      /* Theme editor: selecting a block opens its tile and brings it into view. */
      document.addEventListener(
        'shopify:block:select',
        (event) => {
          const tile = event.target.closest && event.target.closest('[data-scene]');
          if (!tile || !this.contains(tile)) return;
          this.open(tile, 'editor');
          this.goTo(this.tiles.indexOf(tile), false);
        },
        { signal }
      );
      document.addEventListener(
        'shopify:block:deselect',
        (event) => {
          const tile = event.target.closest && event.target.closest('[data-scene]');
          if (tile && this.contains(tile)) this.close(tile);
        },
        { signal }
      );
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    /* Tiles ---------------------------------------------------------------- */

    setupTile(tile, signal) {
      const toggle = tile.querySelector('[data-scene-toggle]');
      const drawer = tile.querySelector('[data-scene-drawer]');
      if (!toggle || !drawer) return;

      tile.litmusScene = { toggle, drawer, source: null, quiet: false };
      this.render(tile, false);

      /* Browsers without overflow: clip can scroll the card to a focused link
         while the drawer is still sliding in. Undo that at once. */
      const card = tile.querySelector('.litmus-scenes__card');
      if (card) {
        card.addEventListener(
          'scroll',
          () => {
            if (card.scrollTop || card.scrollLeft) {
              card.scrollTop = 0;
              card.scrollLeft = 0;
            }
          },
          { passive: true, signal }
        );
      }

      toggle.addEventListener(
        'click',
        () => {
          if (tile.classList.contains('is-open')) {
            this.close(tile);
          } else {
            this.open(tile, 'click');
          }
        },
        { signal }
      );

      tile.addEventListener(
        'mouseenter',
        () => {
          if (this.hoverQuery.matches && !tile.classList.contains('is-open')) this.open(tile, 'hover');
        },
        { signal }
      );

      tile.addEventListener(
        'mouseleave',
        () => {
          if (tile.litmusScene.source === 'hover') this.close(tile);
        },
        { signal }
      );

      /* Keyboard focus only: a mouse click or a tap also focuses the button in
         some browsers, and that must not open the tile before the click does. */
      tile.addEventListener(
        'focusin',
        (event) => {
          if (tile.litmusScene.quiet || tile.classList.contains('is-open')) return;
          if (!this.hoverQuery.matches || !this.isKeyboardFocus(event.target)) return;
          this.open(tile, 'focus');
          this.keepFocusClear(tile);
        },
        { signal }
      );

      tile.addEventListener(
        'focusout',
        (event) => {
          if (tile.litmusScene.source !== 'focus') return;
          if (event.relatedTarget && tile.contains(event.relatedTarget)) return;
          this.close(tile);
        },
        { signal }
      );

      tile.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Escape' || !tile.classList.contains('is-open')) return;
          const focusWasInDrawer = drawer.contains(document.activeElement);
          this.close(tile);
          if (focusWasInDrawer) {
            /* Hand focus back to the toggle without re-opening the tile. */
            tile.litmusScene.quiet = true;
            toggle.focus();
            tile.litmusScene.quiet = false;
          }
        },
        { signal }
      );
    }

    /* Opening slides the toggle up by the drawer's height. Tabbing backwards can leave the
       tile's top under the sticky header, so the slide carried the focused toggle behind it
       (WCAG 2.4.11). Once the slide has ended, scroll the focused element clear. */
    keepFocusClear(tile) {
      const reveal = tile.querySelector('.litmus-scenes__reveal');
      let done = false;
      const check = () => {
        if (done) return;
        done = true;
        const focused = document.activeElement;
        if (!tile.contains(focused)) return;
        const header = document.querySelector('.section-header');
        const clear = Math.max(0, header ? header.getBoundingClientRect().bottom : 0);
        const top = focused.getBoundingClientRect().top;
        if (top < clear) window.scrollBy({ top: top - clear - 16, behavior: 'instant' });
      };
      if (reveal) reveal.addEventListener('transitionend', check, { once: true });
      setTimeout(check, 400);
    }

    isKeyboardFocus(element) {
      try {
        return element.matches(':focus-visible');
      } catch (error) {
        return true;
      }
    }

    open(tile, source) {
      this.tiles.forEach((other) => {
        if (other !== tile && other.classList.contains('is-open')) this.close(other);
      });
      if (!tile.litmusScene) return;
      tile.litmusScene.source = source;
      this.render(tile, true);
    }

    close(tile) {
      if (!tile.litmusScene) return;
      tile.litmusScene.source = null;
      this.render(tile, false);
    }

    render(tile, isOpen) {
      const { toggle, drawer } = tile.litmusScene;
      tile.classList.toggle('is-open', isOpen);
      toggle.setAttribute('aria-expanded', String(isOpen));
      if (isOpen) {
        drawer.removeAttribute('inert');
      } else {
        drawer.setAttribute('inert', '');
      }
    }

    /* Rail ------------------------------------------------------------------ */

    setupRail(signal) {
      this.rail = this.querySelector('[data-scenes-rail]');
      this.prevButton = this.querySelector('[data-scenes-prev]');
      this.nextButton = this.querySelector('[data-scenes-next]');
      this.currentLabel = this.querySelector('[data-scenes-current]');
      this.status = this.querySelector('[data-scenes-status]');
      this.index = 0;
      if (!this.rail || this.tiles.length < 2) return;

      let frame = 0;
      this.rail.addEventListener(
        'scroll',
        () => {
          if (frame) return;
          frame = window.requestAnimationFrame(() => {
            frame = 0;
            this.updateRail();
          });
        },
        { passive: true, signal }
      );

      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.goTo(this.index - 1, true), { signal });
      }
      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.goTo(this.index + 1, true), { signal });
      }

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.updateRail());
        this.resizeObserver.observe(this.rail);
      }
      this.updateRail();
    }

    isScrollable() {
      return this.rail && this.rail.scrollWidth - this.rail.clientWidth > 2;
    }

    updateRail() {
      if (!this.rail || this.tiles.length < 2) return;
      const max = this.rail.scrollWidth - this.rail.clientWidth;
      let left = this.rail.scrollLeft;
      const step = this.tiles[1].offsetLeft - this.tiles[0].offsetLeft;
      let index = step > 0 ? Math.round(left / step) : 0;
      if (max > 0 && left >= max - 2) index = this.tiles.length - 1;

      /* While a button-driven smooth scroll is still travelling, report its
         destination, so quick repeated presses step on from there. */
      if (this.pending) {
        if (Math.abs(left - this.pending.left) <= 2 || performance.now() > this.pending.until) {
          this.pending = null;
        } else {
          index = this.pending.index;
          left = this.pending.left;
        }
      }
      this.index = Math.max(0, Math.min(this.tiles.length - 1, index));

      if (this.currentLabel) this.currentLabel.textContent = String(this.index + 1);
      if (this.prevButton) this.prevButton.setAttribute('aria-disabled', String(left <= 2));
      if (this.nextButton) this.nextButton.setAttribute('aria-disabled', String(max <= 2 || left >= max - 2));
    }

    goTo(index, announce) {
      if (!this.rail || index < 0 || index >= this.tiles.length || !this.isScrollable()) return;
      const max = this.rail.scrollWidth - this.rail.clientWidth;
      const left = Math.min(max, this.tiles[index].offsetLeft - this.tiles[0].offsetLeft);
      this.pending = { index, left, until: performance.now() + 1000 };
      this.updateRail();
      this.rail.scrollTo({ left, behavior: this.motionQuery.matches ? 'auto' : 'smooth' });
      if (announce && this.status) {
        const template = this.dataset.statusTemplate || '[index] / [total]';
        this.status.textContent = template
          .replace('[index]', String(index + 1))
          .replace('[total]', String(this.tiles.length));
      }
    }
  }

  customElements.define('litmus-scenes', LitmusScenes);
})();
